import { React, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { setToken } from '../utils/loginTokens';
import { fetchFlask } from '../utils/fetchFlask';
import { Alerta } from '../components/alerta';
import SettaLogo from '../components/settaLogo';
import styles from '../pages/css/login.module.css';

const inputSx = {
    width: '100%',
    '& .MuiInput-root': {
        position: 'relative',
        color: '#13154F',
        caretColor: '#13154F',
        fontFamily: "'Montserrat', 'Segoe UI', system-ui, sans-serif",
        fontSize: '16px',
        letterSpacing: '0',
        '&:before': {
            borderColor: '#e4e6f5',
            borderWidth: '2px',
            pointerEvents: 'none'
        },
        '&:after': {
            borderColor: '#13154F',
            borderWidth: '2px',
            pointerEvents: 'none'
        },
        ':hover:not(.Mui-focused)': {
            '&:before': {
                borderColor: '#13154F',
                borderWidth: '2px'
            }
        }
    },
    '& .MuiInput-input': {
        position: 'relative',
        zIndex: 1,
        color: '#13154F',
        WebkitTextFillColor: '#13154F',
        caretColor: '#13154F',
        pointerEvents: 'auto',
        backgroundColor: 'transparent',
        opacity: 1,
        padding: '8px 0 9px'
    },
    '& .MuiInputAdornment-root, & .MuiIconButton-root': {
        position: 'relative',
        zIndex: 2
    },
    '& .MuiInput-input::placeholder': {
        color: 'rgba(19, 21, 79, .46)',
        WebkitTextFillColor: 'rgba(19, 21, 79, .46)',
        opacity: 1
    },
    '& .MuiInput-input:-webkit-autofill': {
        color: '#13154F',
        WebkitTextFillColor: '#13154F',
        caretColor: '#13154F',
        boxShadow: '0 0 0 100px rgba(255, 255, 255, .76) inset'
    },
    '& .MuiInput-input:-webkit-autofill:focus': {
        color: '#13154F',
        WebkitTextFillColor: '#13154F',
        caretColor: '#13154F'
    }
};

const Login = ({ isMobile }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const emailInputRef = useRef(null);
    const passwordInputRef = useRef(null);

    async function submitLogin (e) {
        e.preventDefault();

        const normalizedUsername = username.trim().toLowerCase();

        const body = JSON.stringify({
            username: normalizedUsername,
            password
        });

        const response = await fetchFlask('login', body);
        if ('accessToken' in response) {
            setToken(response.accessToken);
        } else {
            setMessage(response.message);
            setStatus(response.status);
            setOpenAlerta(true);
        }
    };

    return (
        <Grid className={styles.loginPage}>
            <Grid id={styles.image} aria-hidden='true'></Grid>
            <Grid id={styles.boxLogin} className={isMobile ? styles.mobileLogin : undefined}>
                <Alerta
                    texto={message}
                    status={status}
                    open={openAlerta}
                    handleClose={() => setOpenAlerta(false)}
                />
                <Box id={styles.loginCard}>
                    <Box id={styles.boxLogo}>
                        <SettaLogo height={44} />
                    </Box>
                    <Typography id={styles.loginTitle}>Acesse sua conta</Typography>
                    <Typography id={styles.loginSubtitle}>Entre com seu e-mail corporativo para continuar.</Typography>
                    <form onSubmit={submitLogin} className={styles.loginForm}>
                        <Box className={styles.boxLabel} onMouseDown={() => emailInputRef.current?.focus()}>
                            <Typography id={styles.emailLabel} >E-mail</Typography>
                            <FormControl sx={{ display: 'block', marginTop: '8px', width: '100%' }}>
                                <TextField
                                    id='login-email'
                                    name='email'
                                    inputRef={emailInputRef}
                                    placeholder='nome@settaenergia.com.br'
                                    variant='standard'
                                    type='email'
                                    sx={inputSx}
                                    inputProps={{
                                        autoCapitalize: 'none',
                                        autoCorrect: 'off',
                                        inputMode: 'email',
                                        spellCheck: 'false'
                                    }}
                                    autoComplete='email'
                                    required
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value.toLowerCase())}
                                />
                            </FormControl>
                        </Box>

                        <Box className={styles.boxLabel} onMouseDown={() => passwordInputRef.current?.focus()}>
                            <Typography id={styles.senhaLabel} >Senha</Typography>
                            <FormControl sx={{ display: 'block', marginTop: '8px', width: '100%' }}>
                                <TextField
                                    id='login-password'
                                    name='password'
                                    inputRef={passwordInputRef}
                                    variant='standard'
                                    sx={inputSx}
                                    required
                                    placeholder='Digite sua senha'
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete='current-password'
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position='end'>
                                                <IconButton
                                                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                                                    onClick={() => setShowPassword((currentValue) => !currentValue)}
                                                    onMouseDown={(event) => event.preventDefault()}
                                                    edge='end'
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </FormControl>
                        </Box>

                        <FormControl sx={{ display: 'block', marginTop: '8px', width: '100%' }}>
                            <Button id={styles.submitLogin} type='submit' variant='contained'>Login</Button>
                        </FormControl>
                    </form>
                </Box>
            </Grid>
        </Grid>
    );
};

export default Login;
